import React, { useState } from "react";
import { Resource, ReadResourceResult } from "@modelcontextprotocol/sdk/types.js";

import { useMCP } from "mcp/hooks/useMCP";

export const MCPResourcesPanel: React.FC = () => {
    const { isCindraConnected, resources, readResource } = useMCP();
    const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
    const [resourceResult, setResourceResult] = useState<ReadResourceResult | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleResourceSelect = (resource: Resource) => {
        setSelectedResource(resource);
        setResourceResult(null);
        setError(null);
    };

    const handleReadResource = async () => {
        if (!selectedResource) return;

        setIsLoading(true);
        setError(null);
        setResourceResult(null);

        try {
            const result = await readResource(selectedResource.uri);
            setResourceResult(result);
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to read resource";
            setError(errorMessage);
        }
        finally {
            setIsLoading(false);
        }
    };

    if (!isCindraConnected) {
        return (
            <div className="bg-white border border-gray-300 rounded-lg shadow p-6">
                <p className="text-gray-500">Connect to an MCP server to see available resources.</p>
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-300 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                MCP Resources ({resources.length})
            </h3>
            {
                resources.length === 0
                    ? (
                        <p className="text-gray-500">No resources available from the connected server.</p>
                    )
                    : (
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="resourceSelect" className="block text-sm font-medium text-gray-700 mb-1">
                                    Select Resource
                                </label>
                                <select
                                    id="resourceSelect"
                                    value={selectedResource?.uri || ""}
                                    onChange={
                                        (e) => {
                                            const resource = resources.find(r => r.uri === e.target.value);
                                            if (resource) handleResourceSelect(resource);
                                        }
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Choose a resource...</option>
                                    {
                                        resources.map((resource) => (
                                            <option key={resource.uri} value={resource.uri}>
                                                {resource.name || resource.uri}
                                            </option>
                                        ))
                                    }
                                </select>
                            </div>
                            {
                                selectedResource && (
                                    <div className="space-y-4">
                                        <div className="bg-gray-50 p-3 rounded-md">
                                            <h4 className="font-medium text-gray-900">{selectedResource.name || "Untitled Resource"}</h4>
                                            <p className="text-sm text-gray-600 mt-1">
                                                <strong>URI:</strong> {selectedResource.uri}
                                            </p>
                                            {
                                                selectedResource.description && (
                                                    <p className="text-sm text-gray-600 mt-1">{selectedResource.description}</p>
                                                )
                                            }
                                            {
                                                selectedResource.mimeType && (
                                                    <p className="text-sm text-gray-600 mt-1">
                                                        <strong>Type:</strong> {selectedResource.mimeType}
                                                    </p>
                                                )
                                            }
                                        </div>
                                        <button
                                            onClick={handleReadResource}
                                            disabled={isLoading}
                                            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                        >
                                            {
                                                isLoading
                                                    ? (
                                                        <>
                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Reading...
                                                        </>
                                                    )
                                                    : (
                                                        "Read Resource"
                                                    )
                                            }
                                        </button>
                                        {
                                            error && (
                                                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                                                    <p className="text-sm text-red-800">Error: {error}</p>
                                                </div>
                                            )
                                        }
                                        {
                                            resourceResult && (
                                                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                                                    <h4 className="font-medium text-blue-800 mb-2">Resource Content:</h4>
                                                    {
                                                        resourceResult.contents.map((content, index) => (
                                                            <div key={index} className="mb-2">
                                                                {
                                                                    'text' in content
                                                                        ? (
                                                                            <pre className="text-sm bg-white p-2 rounded border overflow-x-auto whitespace-pre-wrap">
                                                                                {String(content.text || "")}
                                                                            </pre>
                                                                        )
                                                                        : 'blob' in content
                                                                            ? (
                                                                                <div className="text-sm bg-white p-2 rounded border">
                                                                                    <p className="font-medium mb-1">
                                                                                        Binary content ({content.blob?.length || 0} bytes)
                                                                                    </p>
                                                                                    <p className="text-gray-600">MIME Type: {content.mimeType || "unknown"}</p>
                                                                                </div>
                                                                            )
                                                                            : (
                                                                                <pre className="text-sm bg-white p-2 rounded border overflow-x-auto">
                                                                                    {JSON.stringify(content, null, 2)}
                                                                                </pre>
                                                                            )
                                                                }
                                                            </div>
                                                        ))
                                                    }
                                                </div>
                                            )
                                        }
                                    </div>
                                )
                            }
                        </div>
                    )
            }
        </div>
    );
};